import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { leaseAPI } from '../services/api';
import { FileText, Pen, Download, AlertCircle, Loader2 } from 'lucide-react';

const LeaseSignature = ({ lease, onSignComplete }) => {
  const { toast } = useToast();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureType, setSignatureType] = useState('type');
  const [typedSignature, setTypedSignature] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureStatus, setSignatureStatus] = useState(null);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  const leaseId = lease?.id || lease?._id;

  useEffect(() => {
    if (leaseId) fetchSignatureStatus();
  }, [leaseId]);

  const fetchSignatureStatus = async () => {
    try {
      const response = await leaseAPI.getSignatureStatus(leaseId);
      setSignatureStatus(response.data);
      if (response.data.pending_signatures === 0 && !response.data.is_signed) {
        try {
          await leaseAPI.generateDocument(leaseId);
          const newResponse = await leaseAPI.getSignatureStatus(leaseId);
          setSignatureStatus(newResponse.data);
        } catch (err) {
          console.error('Error generating lease document:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching signature status:', err);
    }
  };

  // Fetch the PDF as a blob when the dialog opens so it renders inline next
  // to the signature pad. The backend now streams the bytes (no S3 redirect),
  // so the Bearer header never leaks to AWS.
  const loadLeasePdf = async () => {
    setIsLoading(true);
    setPdfError(null);
    setPdfUrl(null);
    try {
      const response = await leaseAPI.getUnsignedAgreement(leaseId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error('Error loading lease PDF:', err);
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      setPdfError(typeof detail === 'string' ? detail : 'Failed to load lease agreement');
    } finally {
      setIsLoading(false);
    }
  };

  const openSignDialog = async () => {
    setShowSignDialog(true);
    await loadLeasePdf();
  };

  const closeSignDialog = () => {
    setShowSignDialog(false);
    if (pdfUrl) {
      try { URL.revokeObjectURL(pdfUrl); } catch (_) {}
    }
    setPdfUrl(null);
    setPdfError(null);
    setTypedSignature('');
    clearDrawing();
  };

  // Canvas drawing
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };
  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1a365d';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const stopDrawing = () => setIsDrawing(false);
  const clearDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getSignatureData = () => {
    if (signatureType === 'type') return typedSignature.trim();
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const hasInk = imageData.data.some((p, i) => i % 4 === 3 && p > 0);
      if (hasInk) return canvas.toDataURL('image/png');
    }
    return typedSignature.trim() || lease?.tenant_name || lease?.tenant?.name || '';
  };

  const handleSignLease = async () => {
    const signatureData = getSignatureData();
    if (!signatureData || signatureData.trim() === '') {
      toast({
        title: 'Signature required',
        description: signatureType === 'draw'
          ? 'Please draw your signature or switch to Type mode.'
          : 'Please type your full legal name to continue.',
        variant: 'destructive',
      });
      return;
    }
    try {
      setIsSubmitting(true);
      await leaseAPI.signLease(leaseId, signatureData);
      toast({ title: 'Lease signed ✅', description: 'Your lease agreement has been signed.' });
      closeSignDialog();
      fetchSignatureStatus();
      if (onSignComplete) onSignComplete();
    } catch (err) {
      console.error('Error signing lease:', err);
      let msg = 'Failed to sign lease';
      const d = err?.response?.data?.detail;
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d)) msg = d.map((x) => x.msg || x.message || JSON.stringify(x)).join(', ');
      else if (d && typeof d === 'object') msg = d.msg || d.message || JSON.stringify(d);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadSignedLease = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('access_token') || localStorage.getItem('session_token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const downloadUrl = `${baseUrl}/api/leases/${leaseId}/signed-agreement`;
      try {
        const response = await fetch(downloadUrl, { headers: { Authorization: `Bearer ${token}` } });
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'Signed_Lease_Agreement.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast({ title: 'Success', description: 'Signed lease downloaded.' });
      } catch (fetchError) {
        window.open(`${downloadUrl}?token=${encodeURIComponent(token)}`, '_blank');
        toast({ title: 'Opening PDF', description: 'The lease will open in a new tab.' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to download signed lease.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!lease || !leaseId) return null;

  const isSigned = signatureStatus?.is_signed;
  const hasPendingSignature = signatureStatus?.pending_signatures > 0;

  // Already-signed state: show a download button (both tenant and landlord
  // hit the same /signed-agreement endpoint which now streams bytes server-side).
  if (isSigned) {
    return (
      <Card className="border-2 border-green-300 bg-green-50" data-testid="lease-signed-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <FileText className="h-5 w-5" />
            Lease Agreement Signed
          </CardTitle>
          <CardDescription>Your signed lease is on file.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            onClick={handleDownloadSignedLease}
            disabled={isLoading}
            data-testid="download-signed-lease-btn"
          >
            {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            View / download signed lease
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!hasPendingSignature && signatureStatus !== null) return null;

  return (
    <>
      <Card className="border-2 border-yellow-300 bg-yellow-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-yellow-600" />
            Lease Agreement
          </CardTitle>
          <CardDescription>Your signature is required</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-yellow-700">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Signature required</span>
            </div>
            <p className="text-sm text-gray-700">
              Open the lease to read it in full, then sign at the bottom of the same window to complete your move-in.
            </p>
            <Button
              onClick={openSignDialog}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="review-and-sign-lease-btn"
            >
              <Pen className="h-4 w-4 mr-2" />
              Review &amp; sign lease
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Combined "Read PDF + Sign" dialog */}
      <Dialog open={showSignDialog} onOpenChange={(open) => (open ? setShowSignDialog(true) : closeSignDialog())}>
        <DialogContent className="max-w-5xl max-h-[95vh] p-0 flex flex-col" data-testid="review-and-sign-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle>Review &amp; sign your lease</DialogTitle>
            <DialogDescription>
              Scroll through the lease below, then type or draw your signature and tap Sign lease.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {/* PDF viewer */}
            <div className="border rounded-lg bg-gray-50 overflow-hidden">
              {isLoading && (
                <div className="h-80 flex flex-col items-center justify-center gap-2 text-gray-600" data-testid="lease-pdf-loading">
                  <Loader2 className="h-6 w-6 animate-spin text-green-600" />
                  <span className="text-sm">Loading lease…</span>
                </div>
              )}
              {!isLoading && pdfError && (
                <div className="p-6 space-y-3" data-testid="lease-pdf-error">
                  <div className="flex items-start gap-2 text-red-700">
                    <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">Couldn&apos;t load the lease PDF</div>
                      <div className="text-sm">{pdfError}</div>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={loadLeasePdf}>Retry</Button>
                </div>
              )}
              {!isLoading && !pdfError && pdfUrl && (
                <iframe
                  src={pdfUrl}
                  title="Lease Agreement"
                  className="w-full h-[55vh] bg-white"
                  data-testid="lease-pdf-iframe"
                />
              )}
            </div>

            {/* Signature pad */}
            <div className="space-y-4 border-t pt-4">
              <div>
                <div className="font-semibold text-gray-900 mb-1">Your signature</div>
                <p className="text-xs text-gray-600">
                  By signing below you agree to the terms of the lease shown above. Your electronic signature has the same legal effect as a handwritten one.
                </p>
              </div>

              <Tabs value={signatureType} onValueChange={setSignatureType} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="type" data-testid="sig-type-tab">Type signature</TabsTrigger>
                  <TabsTrigger value="draw" data-testid="sig-draw-tab">Draw signature</TabsTrigger>
                </TabsList>

                <TabsContent value="type" className="space-y-3">
                  <div>
                    <Label>Type your full legal name</Label>
                    <Input
                      value={typedSignature}
                      onChange={(e) => setTypedSignature(e.target.value)}
                      placeholder="Enter your full name"
                      className="text-lg"
                      data-testid="sig-type-input"
                    />
                  </div>
                  {typedSignature && (
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <p className="text-sm text-gray-500 mb-2">Signature preview:</p>
                      <p className="text-2xl italic text-blue-900 font-serif">{typedSignature}</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="draw" className="space-y-3">
                  <div>
                    <Label>Draw your signature below</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        className="w-full cursor-crosshair touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        data-testid="sig-draw-canvas"
                      />
                    </div>
                    <Button variant="ghost" size="sm" onClick={clearDrawing} className="mt-2" data-testid="sig-draw-clear">
                      Clear
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-white">
            <Button variant="outline" onClick={closeSignDialog} disabled={isSubmitting}>Cancel</Button>
            <Button
              onClick={handleSignLease}
              disabled={isSubmitting || (signatureType === 'type' && !typedSignature.trim())}
              className="bg-green-600 hover:bg-green-700"
              data-testid="submit-signature-btn"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Pen className="h-4 w-4 mr-2" />}
              Sign lease
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeaseSignature;
